import { supabase } from '../lib/supabase';

export const adminService = {
  // 1. DASHBOARD OVERVIEW
  async getDashboardStats() {
    try {
      // Get total users count
      const { count: totalUsers } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true });

      // Get active events (published status)
      const { count: activeEvents } = await supabase?.from('events')?.select('*', { count: 'exact', head: true })?.eq('status', 'published');

      // Get pending approvals for events
      const { count: pendingEvents } = await supabase?.from('events')?.select('*', { count: 'exact', head: true })?.eq('status', 'pending');

      // Get pending approvals for marketplace items
      const { count: pendingMarketplace } = await supabase?.from('marketplace_items')?.select('*', { count: 'exact', head: true })?.eq('status', 'pending');

      // Current month boundaries
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString();
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)?.toISOString();

      // Store revenue: orders this month (only completed/paid)
      const { data: storeOrders } = await supabase?.from('orders')
        ?.select('total_amount')
        ?.in('payment_status', ['completed', 'paid'])
        ?.gte('created_at', monthStart)
        ?.lte('created_at', monthEnd);

      const storeRevenue = storeOrders?.reduce((sum, o) => sum + (parseFloat(o?.total_amount) || 0), 0) || 0;

      // Event revenue: only truly paid registrations this month
      // Use created_at (the exact booking/payment timestamp), NOT paid_at or event date
      const { data: eventRegs } = await supabase?.from('event_registrations')
        ?.select('total_price')
        ?.or('status.eq.paid,payment_status.eq.paid,payment_status.eq.completed')
        ?.gte('created_at', monthStart)
        ?.lte('created_at', monthEnd);

      const eventRevenue = eventRegs?.reduce((sum, r) => sum + (parseFloat(r?.total_price) || 0), 0) || 0;

      const totalRevenue = storeRevenue + eventRevenue;

      return {
        totalUsers: totalUsers || 0,
        activeEvents: activeEvents || 0,
        pendingApprovals: (pendingEvents || 0) + (pendingMarketplace || 0),
        totalRevenue,
        storeRevenue,
        eventRevenue
      };
    } catch (error) {
      console.error('Admin stats error:', error);
      return {
        totalUsers: 0,
        activeEvents: 0,
        pendingApprovals: 0,
        totalRevenue: 0,
        storeRevenue: 0,
        eventRevenue: 0
      };
    }
  },

  // Get dashboard stats for a specific month/year
  async getDashboardStatsByMonth(year, month) {
    try {
      const monthStart = new Date(year, month, 1)?.toISOString();
      const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999)?.toISOString();

      // Store revenue for selected month — only completed/paid orders
      const { data: storeOrders } = await supabase?.from('orders')
        ?.select('total_amount')
        ?.in('payment_status', ['completed', 'paid'])
        ?.gte('created_at', monthStart)
        ?.lte('created_at', monthEnd);

      const storeRevenue = storeOrders?.reduce((sum, o) => sum + (parseFloat(o?.total_amount) || 0), 0) || 0;

      // Event revenue for selected month — filter strictly by created_at (booking/payment timestamp)
      // Include status='approved' for historical records (before Aug 2026, paid registrations used 'approved')
      // Include status='paid' for new records (after Aug 2026 migration added 'paid' enum value)
      const { data: eventRegsAll } = await supabase?.from('event_registrations')
        ?.select('total_price, created_at, status, payment_status');

      // JS-side filtering: robust date parsing with column fallback + correct status check
      const selectedYear = year;
      const selectedMonthNumber = month + 1; // convert 0-indexed to 1-indexed

      const eventRevenue = (eventRegsAll || [])?.reduce((sum, reg) => {
        // Fallback across possible timestamp column names
        const dateValue = reg?.created_at || reg?.createdAt || reg?.inserted_at || reg?.registration_date;
        if (!dateValue) return sum;

        const rowDate = new Date(dateValue);
        const isSameMonth = rowDate?.getMonth() + 1 === selectedMonthNumber;
        const isSameYear = rowDate?.getFullYear() === selectedYear;

        if (!isSameMonth || !isSameYear) return sum;

        // Accept both old 'approved' status (pre-Aug 2026) and new 'paid' status (post-Aug 2026)
        const isPaid =
          reg?.status === 'paid' ||
          reg?.status === 'approved' ||
          reg?.payment_status === 'paid' ||
          reg?.payment_status === 'completed';

        if (!isPaid) return sum;

        return sum + (parseFloat(reg?.total_price) || 0);
      }, 0);

      const totalRevenue = storeRevenue + eventRevenue;

      // Get total users count (always current)
      const { count: totalUsers } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true });

      // Get active events (always current)
      const { count: activeEvents } = await supabase?.from('events')?.select('*', { count: 'exact', head: true })?.eq('status', 'published');

      // Get pending approvals (always current)
      const { count: pendingEvents } = await supabase?.from('events')?.select('*', { count: 'exact', head: true })?.eq('status', 'pending');
      const { count: pendingMarketplace } = await supabase?.from('marketplace_items')?.select('*', { count: 'exact', head: true })?.eq('status', 'pending');

      return {
        totalUsers: totalUsers || 0,
        activeEvents: activeEvents || 0,
        pendingApprovals: (pendingEvents || 0) + (pendingMarketplace || 0),
        totalRevenue,
        storeRevenue,
        eventRevenue
      };
    } catch (error) {
      console.error('Admin stats by month error:', error);
      return {
        totalUsers: 0,
        activeEvents: 0,
        pendingApprovals: 0,
        totalRevenue: 0,
        storeRevenue: 0,
        eventRevenue: 0
      };
    }
  },

  // Get all-time total revenue (store + event)
  async getAllTimeRevenue() {
    try {
      const { data: storeOrders } = await supabase?.from('orders')
        ?.select('total_amount')
        ?.in('payment_status', ['completed', 'paid']);

      const allTimeStoreRevenue = storeOrders?.reduce((sum, o) => sum + (parseFloat(o?.total_amount) || 0), 0) || 0;

      const { data: eventRegs } = await supabase?.from('event_registrations')
        ?.select('total_price')
        ?.or('status.eq.paid,payment_status.eq.paid,payment_status.eq.completed');

      const allTimeEventRevenue = eventRegs?.reduce((sum, r) => sum + (parseFloat(r?.total_price) || 0), 0) || 0;

      return {
        allTimeTotal: allTimeStoreRevenue + allTimeEventRevenue,
        allTimeStoreRevenue,
        allTimeEventRevenue
      };
    } catch (error) {
      console.error('All-time revenue error:', error);
      return { allTimeTotal: 0, allTimeStoreRevenue: 0, allTimeEventRevenue: 0 };
    }
  },

  // Get detailed transaction list for a specific month/year (for the Aylık Gelir Detayı modal)
  async getMonthlyRevenueTransactions(year, month) {
    try {
      const monthStart = new Date(year, month, 1)?.toISOString();
      const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999)?.toISOString();

      // Store orders for the month
      const { data: storeOrders } = await supabase?.from('orders')
        ?.select('id, created_at, full_name, order_number, payment_method, total_amount, payment_status')
        ?.in('payment_status', ['completed', 'paid'])
        ?.gte('created_at', monthStart)
        ?.lte('created_at', monthEnd)
        ?.order('created_at', { ascending: false });

      // Fetch ALL paid event registrations without date filter — apply JS-side date filtering
      // to handle column name variations and correct 0-indexed month comparison
      const { data: eventRegsAll } = await supabase?.from('event_registrations')
        ?.select('id, created_at, participant_name, payment_method, total_price, status, payment_status, event_id, events(title)');

      const selectedYear = year;
      const selectedMonthNumber = month + 1; // convert 0-indexed to 1-indexed

      const filteredEventRegs = (eventRegsAll || [])?.filter(reg => {
        // Fallback across possible timestamp column names
        const dateValue = reg?.created_at || reg?.createdAt || reg?.inserted_at || reg?.registration_date;
        if (!dateValue) return false;

        const rowDate = new Date(dateValue);
        const isSameMonth = rowDate?.getMonth() + 1 === selectedMonthNumber;
        const isSameYear = rowDate?.getFullYear() === selectedYear;

        if (!isSameMonth || !isSameYear) return false;

        // Accept both old 'approved' status (pre-Aug 2026) and new 'paid' status (post-Aug 2026)
        return (
          reg?.status === 'paid' ||
          reg?.status === 'approved' ||
          reg?.payment_status === 'paid' ||
          reg?.payment_status === 'completed'
        );
      });

      const storeTransactions = (storeOrders || [])?.map(o => ({
        id: o?.id,
        date: o?.created_at,
        category: 'Mağaza',
        customer: o?.full_name || o?.order_number || '-',
        paymentMethod: o?.payment_method || '-',
        amount: parseFloat(o?.total_amount) || 0
      }));

      const eventTransactions = filteredEventRegs?.map(r => ({
        id: r?.id,
        date: r?.created_at || r?.createdAt || r?.inserted_at || r?.registration_date,
        category: 'Etkinlik',
        customer: `${r?.participant_name || '-'} — ${r?.events?.title || 'Etkinlik'}`,
        paymentMethod: r?.payment_method || '-',
        amount: parseFloat(r?.total_price) || 0
      }));

      const allTransactions = [...storeTransactions, ...eventTransactions]
        ?.sort((a, b) => new Date(b?.date) - new Date(a?.date));

      return allTransactions;
    } catch (error) {
      console.error('Monthly revenue transactions error:', error);
      return [];
    }
  },

  async getRevenueChartData() {
    try {
      // Get last 6 months of orders and event registrations
      const sixMonthsAgo = new Date();
      sixMonthsAgo?.setMonth(sixMonthsAgo?.getMonth() - 6);

      const [{ data: storeOrders }, { data: eventRegs }] = await Promise.all([
        supabase?.from('orders')
          ?.select('created_at, total_amount')
          ?.gte('created_at', sixMonthsAgo?.toISOString())
          ?.neq('status', 'cancelled'),
        supabase?.from('event_registrations')
          ?.select('created_at, total_price')
          ?.gte('created_at', sixMonthsAgo?.toISOString())
          ?.or('status.eq.approved,payment_status.eq.Ödendi')
      ]);

      // Build ordered month keys for last 6 months
      const monthKeys = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d?.setMonth(d?.getMonth() - i);
        monthKeys?.push(d?.toLocaleString('tr-TR', { month: 'short', year: '2-digit' }));
      }

      const storeByMonth = {};
      const eventByMonth = {};
      monthKeys?.forEach(k => { storeByMonth[k] = 0; eventByMonth[k] = 0; });

      storeOrders?.forEach(order => {
        const d = new Date(order.created_at);
        const key = d?.toLocaleString('tr-TR', { month: 'short', year: '2-digit' });
        if (storeByMonth?.[key] !== undefined) {
          storeByMonth[key] += parseFloat(order?.total_amount) || 0;
        }
      });

      eventRegs?.forEach(reg => {
        const d = new Date(reg.created_at);
        const key = d?.toLocaleString('tr-TR', { month: 'short', year: '2-digit' });
        if (eventByMonth?.[key] !== undefined) {
          eventByMonth[key] += parseFloat(reg?.total_price) || 0;
        }
      });

      return monthKeys?.map(key => ({
        month: key,
        storeRevenue: Math.round(storeByMonth?.[key]),
        eventRevenue: Math.round(eventByMonth?.[key]),
        revenue: Math.round(storeByMonth?.[key] + eventByMonth?.[key])
      }));
    } catch (error) {
      console.error('Revenue chart error:', error);
      return [];
    }
  },

  // 2. USER MANAGEMENT
  async getAllUsers() {
    try {
      // Use Edge Function to get auth.users (real emails + names) merged with user_profiles
      const { data: { session } } = await supabase?.auth?.getSession();
      
      if (session?.access_token) {
        try {
          const response = await fetch(
            `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/get-admin-users`,
            {
              headers: {
                'Authorization': `Bearer ${session?.access_token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response?.ok) {
            const result = await response?.json();
            if (result?.users) {
              return result?.users;
            }
          }
        } catch (edgeFnError) {
          console.warn('Edge function failed, falling back to direct query:', edgeFnError);
        }
      }

      // Fallback: direct user_profiles query
      const { data, error } = await supabase?.from('user_profiles')?.select('*, is_banned')?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(user => {
        const fullName = user?.full_name?.trim();
        const firstLast = [user?.first_name, user?.last_name]?.filter(Boolean)?.join(' ')?.trim();
        const displayName = fullName || firstLast || 'İsimsiz';

        return {
          id: user?.id,
          name: displayName,
          email: user?.email,
          avatar: user?.avatar_url,
          role: user?.role,
          status: user?.is_banned ? 'banned' : (user?.membership_tier === 'free' ? 'active' : user?.membership_tier),
          joinDate: new Date(user.created_at)?.toLocaleDateString('tr-TR'),
          phone: user?.phone,
          location: user?.location,
          bio: user?.bio,
          experienceLevel: user?.experience_level,
          membershipTier: user?.membership_tier,
          isBanned: user?.is_banned,
          city: user?.city,
          district: user?.district,
          addressLine: user?.address_line,
          tc_kimlik: user?.tc_kimlik,
          tc: user?.tc,
          vkn: user?.vkn,
          address: user?.address,
          full_address: user?.full_address,
          raw_user_meta_data: user?.raw_user_meta_data,
        };
      }) || [];
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  },

  async updateUserStatus(userId, updates) {
    try {
      // Use RPC function for role updates to bypass recursive RLS policy
      if (updates?.role !== undefined) {
        const { data, error } = await supabase?.rpc('admin_update_user_role', {
          target_user_id: userId,
          new_role: updates?.role
        });

        if (error) {
          console.error('RPC role update error:', error);
          throw error;
        }

        if (data === false) {
          throw new Error('Rol güncellenemedi. Yetkiniz olmayabilir.');
        }

        console.log('Role updated successfully via RPC:', data);
      }

      // Handle other updates (non-role fields) via direct update
      const otherUpdates = {};
      if (updates?.membershipTier !== undefined) otherUpdates.membership_tier = updates?.membershipTier;

      if (Object.keys(otherUpdates)?.length > 0) {
        const { data, error } = await supabase?.from('user_profiles')?.update(otherUpdates)?.eq('id', userId)?.select();

        if (error) {
          console.error('Supabase update error:', error);
          throw error;
        }

        console.log('Other fields updated successfully:', data);
      }

      return true;
    } catch (error) {
      console.error('Update user status error:', error);
      throw error;
    }
  },

  async toggleProMembership(userId, currentTier) {
    try {
      const newTier = currentTier === 'pro' ? 'free' : 'pro';
      
      const { data, error } = await supabase?.from('user_profiles')?.update({ 
          membership_tier: newTier
        })?.eq('id', userId)?.select();

      if (error) {
        console.error('Toggle PRO membership error:', error);
        throw error;
      }
      
      console.log('PRO membership toggled successfully:', data);
      return { success: true, newTier };
    } catch (error) {
      console.error('Toggle PRO membership error:', error);
      throw error;
    }
  },

  async banUser(userId) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ 
          is_banned: true
        })?.eq('id', userId);

      if (error) {
        console.error('Ban user error:', error);
        throw error;
      }
      
      console.log('User banned successfully');
      return { success: true };
    } catch (error) {
      console.error('Ban user error:', error);
      throw error;
    }
  },

  async unbanUser(userId) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ 
          is_banned: false
        })?.eq('id', userId);

      if (error) {
        console.error('Unban user error:', error);
        throw error;
      }
      
      console.log('User unbanned successfully');
      return { success: true };
    } catch (error) {
      console.error('Unban user error:', error);
      throw error;
    }
  },

  // 3. MODERATION (Pending Items)
  async getPendingContent() {
    try {
      // Fetch pending events with organizer details
      const { data: events } = await supabase?.from('events')?.select(`
          id,
          title,
          created_at,
          organizer:user_profiles!organizer_id(full_name)
        `)?.eq('status', 'pending');

      // Fetch pending marketplace items with seller details
      const { data: items } = await supabase?.from('marketplace_items')?.select(`
          id,
          title,
          created_at,
          seller:user_profiles!seller_id(full_name)
        `)?.eq('status', 'pending');

      return {
        events: events?.map(e => ({
          id: e?.id,
          type: 'event',
          title: e?.title,
          author: e?.organizer?.full_name || 'Unknown',
          date: new Date(e.created_at)?.toLocaleDateString('tr-TR'),
          status: 'pending'
        })) || [],
        marketplace: items?.map(i => ({
          id: i?.id,
          type: 'marketplace',
          title: i?.title,
          author: i?.seller?.full_name || 'Unknown',
          date: new Date(i.created_at)?.toLocaleDateString('tr-TR'),
          status: 'pending'
        })) || []
      };
    } catch (error) {
      console.error('Get pending content error:', error);
      return { events: [], marketplace: [] };
    }
  },

  async reviewContent(id, type, decision) {
    try {
      const table = type === 'event' ? 'events' : 'marketplace_items';
      const status = decision === 'approve' ? (type === 'event' ? 'published' : 'active') : 'rejected';

      const { error } = await supabase?.from(table)?.update({ status })?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Review content error:', error);
      throw error;
    }
  },

  // 4. BADGE MANAGEMENT
  async getUserAchievements(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('achievements')?.eq('id', userId)?.single();

      if (error) throw error;
      return data?.achievements || [];
    } catch (error) {
      console.error('Get user achievements error:', error);
      return [];
    }
  },

  async updateUserAchievements(userId, achievements) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ achievements })?.eq('id', userId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Update user achievements error:', error);
      throw error;
    }
  },

  async awardBadge(userId, badgeId, badgeData) {
    try {
      // Get current achievements
      const achievements = await this.getUserAchievements(userId);
      
      // Check if badge already exists
      const existingIndex = achievements?.findIndex(a => a?.id === badgeId);
      
      if (existingIndex >= 0) {
        // Update existing badge
        achievements[existingIndex] = {
          ...achievements?.[existingIndex],
          ...badgeData,
          unlocked: true,
          unlockedDate: new Date()?.toISOString()
        };
      } else {
        // Add new badge
        achievements?.push({
          ...badgeData,
          id: badgeId,
          unlocked: true,
          unlockedDate: new Date()?.toISOString()
        });
      }
      
      await this.updateUserAchievements(userId, achievements);
      return true;
    } catch (error) {
      console.error('Award badge error:', error);
      throw error;
    }
  },

  async revokeBadge(userId, badgeId) {
    try {
      // Get current achievements
      const achievements = await this.getUserAchievements(userId);
      
      // Find and update the badge
      const updatedAchievements = achievements?.map(achievement => {
        if (achievement?.id === badgeId) {
          return {
            ...achievement,
            unlocked: false,
            unlockedDate: null
          };
        }
        return achievement;
      });
      
      await this.updateUserAchievements(userId, updatedAchievements);
      return true;
    } catch (error) {
      console.error('Revoke badge error:', error);
      throw error;
    }
  },

  // 5. USER DELETION
  async deleteUser(userId) {
    try {
      // Use the database function instead of auth.admin API
      // This function runs with SECURITY DEFINER privileges
      const { data, error } = await supabase?.rpc('admin_delete_user', {
        target_user_id: userId
      });
      
      if (error) {
        console.error('Delete user error:', error);
        
        // Handle specific error messages
        if (error?.message?.includes('Only admins')) {
          throw new Error('Sadece adminler kullanıcı silebilir');
        } else if (error?.message?.includes('Cannot delete your own')) {
          throw new Error('Kendi hesabınızı silemezsiniz');
        } else if (error?.message?.includes('User not found')) {
          throw new Error('Kullanıcı bulunamadı');
        } else {
          throw new Error('Kullanıcı silinemedi: ' + error?.message);
        }
      }

      console.log('User deleted successfully:', userId);
      return { success: true };
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  }
};